/**
 * Single source of truth for "Last updated" dates.
 *
 * Both the visible <LastUpdated /> stamp shown on a page AND the
 * <lastmod> emitted into the sitemap for that page read from here.
 * That way the two values can never disagree, and we never auto-bump
 * lastmod to "today" on a page whose content didn't actually change
 * (which Google treats as a noisy/untrustworthy signal).
 *
 * Section data already owns its own lastmod:
 *   - Articles  → `articleLastmod()` from `learningHub.ts`
 *   - Cities    → `cityLastmod()` from `citiesData.ts`
 *   - Industries → `industryLastmod()` from `industriesData.ts`
 *
 * This registry covers everything else: the home page, service hubs,
 * trust/about/legal pages, and any static spoke whose content was last
 * touched on a specific known date. When a page isn't listed here it
 * falls back to SITE_FALLBACK_UPDATED — NEVER `new Date()`.
 *
 * Bumping a date:
 *   • You changed the page's real content → bump that page's entry.
 *   • You did a site-wide template/copy refresh → bump SITE_FALLBACK_UPDATED.
 */

import {
  ARTICLES,
  articleLastmod,
  ARTICLE_DEFAULT_UPDATED,
} from "@/lib/learningHub";
import { INDUSTRY_ADVICE } from "@/lib/industryAdviceData";
import {
  INDUSTRY_PAGES,
  industryLastmod,
  INDUSTRY_DEFAULT_UPDATED,
} from "@/lib/industriesData";
import {
  CITY_PAGES,
  cityLastmod,
  CITY_DEFAULT_UPDATED,
} from "@/lib/citiesData";

/**
 * The "site-wide" fallback. Bump this when a template-wide refresh
 * touches the look/feel of every page (new nav, new footer, new hero
 * pattern, etc.). Do NOT bump it just because today is a new day.
 */
export const SITE_FALLBACK_UPDATED = "2026-05-30";

/**
 * Per-page overrides. Key = canonical path (no host, no trailing slash,
 * `/` for the home page). Value = ISO date string (YYYY-MM-DD).
 *
 * Only add an entry when the page's real content was last edited on a
 * specific date. Otherwise, the page inherits SITE_FALLBACK_UPDATED.
 */
export const PAGE_LAST_UPDATED: Record<string, string> = {
  ...Object.fromEntries(Object.keys(INDUSTRY_ADVICE).map((path) => [path, "2026-10-17"])),
  "/": "2026-05-30",
  "/seo-companies-near-me": "2026-10-17",
  "/nonprofit-website-design-colorado": "2026-10-17",
  "/crm-setup-colorado": "2026-10-17",
  "/business-photography-videography-colorado": "2026-10-17",
  "/marketing-consulting-colorado": "2026-10-17",
  "/best-seo-company-colorado": "2026-10-17",
  "/affordable-seo-small-business-colorado": "2026-10-17",
  "/web-designers-near-me": "2026-10-17",
  "/social-media-marketing-small-business-colorado": "2026-10-17",
  "/marketing-peyton": "2026-10-17",
  "/marketing-calhan": "2026-10-17",
  "/marketing-ellicott": "2026-10-17",
  "/marketing-pueblo-west": "2026-10-17",
  "/marketing-colorado-city": "2026-10-17",
  "/marketing-castle-pines": "2026-10-17",
  "/marketing-larkspur": "2026-10-17",
  "/marketing-lone-tree": "2026-10-17",
  "/restoration-marketing-colorado": "2026-10-17",
  "/tree-service-marketing-colorado": "2026-10-17",
  "/painting-marketing-colorado": "2026-10-17",
  "/garage-door-marketing-colorado": "2026-10-17",
};

/** Normalize a path or absolute URL down to the registry key form. */
function toKey(pathOrUrl: string): string {
  let p = pathOrUrl;
  // Strip protocol+host if a full URL was passed.
  const hostMatch = p.match(/^https?:\/\/[^/]+(.*)$/);
  if (hostMatch) p = hostMatch[1] || "/";
  if (!p.startsWith("/")) p = `/${p}`;
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

/**
 * Resolve the last-updated date for a page by path. Honors:
 *   1. Explicit registry entry (PAGE_LAST_UPDATED)
 *   2. SITE_FALLBACK_UPDATED
 */
export function getPageLastUpdated(pathOrUrl: string): string {
  return PAGE_LAST_UPDATED[toKey(pathOrUrl)] ?? SITE_FALLBACK_UPDATED;
}

/**
 * Max lastmod across every section + the registry. Used for the
 * sitemap-index `<lastmod>` so it reflects the most recent real
 * content change anywhere on the site (never just `today`).
 */
export function getSiteWideLastUpdated(): string {
  let max = SITE_FALLBACK_UPDATED;
  for (const v of Object.values(PAGE_LAST_UPDATED)) if (v > max) max = v;

  const articleMax = ARTICLES.reduce(
    (acc, a) => (articleLastmod(a) > acc ? articleLastmod(a) : acc),
    ARTICLE_DEFAULT_UPDATED,
  );
  if (articleMax > max) max = articleMax;

  const cityMax = CITY_PAGES.reduce(
    (acc, c) => (cityLastmod(c) > acc ? cityLastmod(c) : acc),
    CITY_DEFAULT_UPDATED,
  );
  if (cityMax > max) max = cityMax;

  const industryMax = INDUSTRY_PAGES.reduce(
    (acc, i) => (industryLastmod(i) > acc ? industryLastmod(i) : acc),
    INDUSTRY_DEFAULT_UPDATED,
  );
  if (industryMax > max) max = industryMax;

  return max;
}
